import React, { useState } from 'react';
import { AttachmentPicker } from '../../components';
import TicketLocationModal from './components/TicketLocationModal';
import {
    Card,
    CardBody,
    Typography,
    Button,
    Chip,
    Select,
    Option,
    Avatar,
    IconButton,
    Menu,
    MenuHandler,
    MenuList,
    MenuItem,
    Alert,
    Breadcrumbs,
    Spinner,
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Textarea,
    Input,
    Rating,
} from "@material-tailwind/react";
import {
    User,
    Mail,
    Phone,
    Calendar,
    Tag,
    AlertCircle,
    // CheckCircle,
    MoreVertical,
    Edit,
    UserPlus,
    AlertTriangle,
    Info,
    ChevronUp,
    ChevronDown,
    Flag,
    Zap,
    Shield,
    Paperclip,
    Download,
    MapPinIcon,
    Hash,
    HomeIcon,
    LifeBuoyIcon,
    InfoIcon,
    ClockIcon,
    CheckCircleIcon,
    XCircleIcon,
    RefreshCw,
    Send,
    UserCheck,
    CheckCheck,
} from "lucide-react";
import moment from 'moment';
import { Link, useParams } from 'react-router-dom';
import {
    useAddSupportTicketInteraction,
    useAssignTicket,
    useGetSupportTicketDetails,
    useUpdateTicketDueDate,
    useUpdateTicketLocation,
    useUpdateTicketPriority,
    useUpdateTicketStatus,
} from '../../custom-hooks/use-support';
import { useGetUsers } from '../../custom-hooks/use-users';
import { STATIC_USER_ROLES, SUPPORT_TICKET_PRIORITIES_ENUM, SUPPORT_TICKET_STATUSES } from '../../utils/enums';
import { convertToPresentableText } from '../../utils/functions';

const getStatusConfig = (status) => {
    switch (status) {
        case 'open':
            return { color: 'blue', icon: <InfoIcon /> };
        case 'in_progress':
            return { color: 'amber', icon: <ClockIcon /> };
        case 'resolved':
            return { color: 'green', icon: <CheckCircleIcon /> };
        case 'closed':
            return { color: 'gray', icon: <XCircleIcon /> };
        default:
            return { color: 'gray', icon: <InfoIcon /> };
    }
};

const getPriorityConfig = (priority) => {
    switch (priority) {
        case 'critical':
            return { color: 'red', icon: <Zap className="h-4 w-4" />, label: 'Critical' };
        case 'urgent':
            return { color: 'red', icon: <Zap className="h-4 w-4" />, label: 'Urgent' };
        case 'high':
            return { color: 'orange', icon: <ChevronUp className="h-4 w-4" />, label: 'High' };
        case 'medium':
            return { color: 'amber', icon: <Flag className="h-4 w-4" />, label: 'Medium' };
        case 'low':
            return { color: 'blue', icon: <ChevronDown className="h-4 w-4" />, label: 'Low' };
        default:
            return { color: 'gray', icon: <Flag className="h-4 w-4" />, label: 'Unknown' };
    }
};

const getCategoryConfig = (category) => {
    const categories = {
        technical: { color: 'purple', icon: <AlertCircle className="h-4 w-4" /> },
        billing: { color: 'green', icon: <Calendar className="h-4 w-4" /> },
        general: { color: 'blue', icon: <Info className="h-4 w-4" /> },
        complaint: { color: 'red', icon: <AlertTriangle className="h-4 w-4" /> },
        feature_request: { color: 'indigo', icon: <ChevronUp className="h-4 w-4" /> },
    };

    return categories[category] || { color: 'gray', icon: <Tag className="h-4 w-4" /> };
};

const formatDate = (date) => moment(date).format('MMM DD, YYYY hh:mm A');
const formatTimeAgo = (date) => moment(date).fromNow();

const SupportDetailPage = () => {
    const { ticketId } = useParams();

    const { data, isLoading, error: isError, refetch } = useGetSupportTicketDetails(ticketId, 15 * 1000);
    const ticket = data?.ticket || {};
    const interactions = data?.interactions || [];

    const { data: agents, isLoading: agentsLoading } = useGetUsers({
        role: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.MANAGER, STATIC_USER_ROLES.EMPLOYEE, STATIC_USER_ROLES.STAFF],
        _page: 1,
        _limit: 1000,
    });

    const addInteractionMutation = useAddSupportTicketInteraction();
    const updateTicketStatusMutation = useUpdateTicketStatus();
    const assignTicketMutation = useAssignTicket();
    const updateTicketDueDateMutation = useUpdateTicketDueDate();
    const updateTicketPriorityMutation = useUpdateTicketPriority();
    const updateTicketLocationMutation = useUpdateTicketLocation();

    // const [selectedStatus, setSelectedStatus] = useState('open');
    const [activeTab, setActiveTab] = useState('interactions');
    const [newMessage, setNewMessage] = useState('');
    const [messageAttachments, setMessageAttachments] = useState([]);
    const [locationDialogOpen, setLocationDialogOpen] = useState(false);
    // Set when a status change was blocked for want of a location — the pending status is
    // held here so confirming the modal completes the original action in one request.
    const [pendingStatusChange, setPendingStatusChange] = useState(null);
    const [priorityDialogOpen, setPriorityDialogOpen] = useState(false);
    const [newPriority, setNewPriority] = useState('');
    const [statusDialogOpen, setStatusDialogOpen] = useState(false);
    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const [dueDateDialogOpen, setDueDateDialogOpen] = useState(false);
    const [newStatus, setNewStatus] = useState('');
    const [resolution, setResolution] = useState('');
    const [selectedAgent, setSelectedAgent] = useState('');
    const [dueDate, setDueDate] = useState('');

    const openStatusDialog = () => {
        setNewStatus(ticket?.status || 'open');
        setResolution(ticket?.resolution || '');
        setStatusDialogOpen(true);
    };

    const openAssignDialog = () => {
        setSelectedAgent(ticket?.assignedAgent?._id || '');
        setAssignDialogOpen(true);
    };

    const openDueDateDialog = () => {
        setDueDate(ticket?.dueDate ? moment(ticket.dueDate).format('YYYY-MM-DD') : '');
        setDueDateDialogOpen(true);
    };

    const openPriorityDialog = () => {
        setNewPriority(ticket?.priority || '');
        setPriorityDialogOpen(true);
    };

    const submitStatusUpdate = async () => {
        if (!ticket?._id || !newStatus) return;

        // No location yet — hand off to the modal rather than firing a request the
        // server will reject. The server enforces this too; this just avoids the round
        // trip and lets the agent fix it in place.
        if (!ticket?.floor) {
            setStatusDialogOpen(false);
            setPendingStatusChange({ status: newStatus, resolution: resolution || '' });
            setLocationDialogOpen(true);
            return;
        }

        await updateTicketStatusMutation.mutateAsync({
            ticketId: ticket._id,
            data: { status: newStatus, resolution: resolution || '' },
        });
        await refetch();
        setStatusDialogOpen(false);
    };

    const closeLocationDialog = () => {
        setLocationDialogOpen(false);
        setPendingStatusChange(null);
    };

    const submitLocation = async (location) => {
        if (!ticket?._id) return;

        if (pendingStatusChange) {
            // Location and status in a single request, so confirming the gate is one
            // atomic save rather than two that could half-fail.
            await updateTicketStatusMutation.mutateAsync({
                ticketId: ticket._id,
                data: { ...pendingStatusChange, ...location },
            });
        } else {
            await updateTicketLocationMutation.mutateAsync({ ticketId: ticket._id, data: location });
        }
        await refetch();
        closeLocationDialog();
    };

    const submitAssignment = async () => {
        if (!ticket?._id || !selectedAgent) return;
        await assignTicketMutation.mutateAsync({
            ticketId: ticket._id,
            data: { agentId: selectedAgent },
        });
        await refetch();
        setAssignDialogOpen(false);
    };

    const submitSetDueDate = async () => {
        if (!ticket?._id || !dueDate) return;
        await updateTicketDueDateMutation.mutateAsync({
            ticketId: ticket._id,
            data: { dueDate },
        });
        await refetch();
        setDueDateDialogOpen(false);
    };

    const submitPriorityUpdate = async () => {
        if (!ticket?._id || !newPriority) return;
        await updateTicketPriorityMutation.mutateAsync({
            ticketId: ticket._id,
            data: { priority: newPriority },
        });
        await refetch();
        setPriorityDialogOpen(false);
    };

    const handleSendMessage = async () => {
        if ((!newMessage.trim() && messageAttachments.length === 0) || !ticketId) return;
        await addInteractionMutation.mutateAsync({
            ticketId,
            data: {
                message: newMessage,
                interactionType: 'note',
                isInternal: false,
                attachments: messageAttachments,
            },
        });
        setNewMessage('');
        setMessageAttachments([]);
        refetch();
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
                    <Typography className="mt-4 text-gray-600">Loading ticket details...</Typography>
                </div>
            </div>
        );
    }

    if (isError) {
        return (
            <Alert color="red" className="m-4">
                <AlertCircle className="h-4 w-4" />
                <span className="ml-2">Error loading ticket details. Please try again.</span>
            </Alert>
        );
    }

    const statusConfig = getStatusConfig(ticket?.status);
    const priorityConfig = getPriorityConfig(ticket?.priority);
    const categoryConfig = getCategoryConfig(ticket?.category);

    const statusToneClasses = {
        open: 'bg-blue-50 text-blue-700 border-blue-100',
        in_progress: 'bg-amber-50 text-amber-700 border-amber-100',
        resolved: 'bg-green-50 text-green-700 border-green-100',
        closed: 'bg-gray-100 text-gray-700 border-gray-200',
    };

    const priorityToneClasses = {
        critical: 'bg-red-50 text-red-700 border-red-100',
        urgent: 'bg-red-50 text-red-700 border-red-100',
        high: 'bg-orange-50 text-orange-700 border-orange-100',
        medium: 'bg-amber-50 text-amber-700 border-amber-100',
        low: 'bg-blue-50 text-blue-700 border-blue-100',
    };

    const categoryToneClasses = {
        technical: 'bg-indigo-50 text-indigo-700 border-indigo-100',
        billing: 'bg-emerald-50 text-emerald-700 border-emerald-100',
        general: 'bg-sky-50 text-sky-700 border-sky-100',
        complaint: 'bg-rose-50 text-rose-700 border-rose-100',
        feature_request: 'bg-violet-50 text-violet-700 border-violet-100',
    };

    return (
        <>
            <div className="p-4 m-4 border rounded-3xl">
                <div className="flex items-start justify-between gap-3 mb-4">
                    <Breadcrumbs>
                        <Link to="/" className="opacity-60 flex items-center gap-1">
                            <HomeIcon size={14} />
                            <span>Home</span>
                        </Link>
                        <Link to="/support" className="opacity-60 flex items-center gap-1">
                            <LifeBuoyIcon size={14} />
                            <span>Support</span>
                        </Link>
                        <span className="flex items-center gap-1">
                            {isLoading ? (
                                <Spinner size="sm" />
                            ) : (
                                <>
                                    <LifeBuoyIcon size={14} />
                                    <span>{convertToPresentableText(ticket?.subject?.slice(0, 20)) || 'Ticket Details'}</span>
                                    {ticket?.subject?.length > 20 && <span>...</span>}
                                </>
                            )}
                        </span>
                    </Breadcrumbs>

                    <div className="flex items-center gap-2">
                        <IconButton variant="outlined" size="sm" onClick={() => refetch()}>
                            <RefreshCw className="h-4 w-4" />
                        </IconButton>
                        <Menu>
                            <MenuHandler>
                                <IconButton variant="outlined" size="sm">
                                    <MoreVertical className="h-4 w-4" />
                                </IconButton>
                            </MenuHandler>
                            <MenuList>
                                <MenuItem className="flex items-center gap-2" onClick={openStatusDialog}>
                                    <Edit className="h-4 w-4" />
                                    Update Status
                                </MenuItem>
                                <MenuItem className="flex items-center gap-2" onClick={() => setLocationDialogOpen(true)}>
                                    <MapPinIcon className="h-4 w-4" />
                                    {ticket?.floor ? 'Edit Location' : 'Set Location'}
                                </MenuItem>
                                <MenuItem className="flex items-center gap-2" onClick={openPriorityDialog}>
                                    <Flag className="h-4 w-4" />
                                    Update Priority
                                </MenuItem>
                                <MenuItem className="flex items-center gap-2" onClick={openAssignDialog}>
                                    <UserCheck className="h-4 w-4" />
                                    Assign Agent
                                </MenuItem>
                                {[SUPPORT_TICKET_STATUSES.OPEN, SUPPORT_TICKET_STATUSES.IN_PROGRESS].includes(ticket?.status) && (
                                    <MenuItem className="flex items-center gap-2" onClick={openDueDateDialog}>
                                        <Calendar className="h-4 w-4" />
                                        Set Due Date
                                    </MenuItem>
                                )}
                            </MenuList>
                        </Menu>
                    </div>
                </div>

                <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
                    <div className='col-span-2 space-y-4'>
                        <div>
                            <Typography variant="small" className="text-gray-500 mb-2">Subject</Typography>
                            <Typography variant="h5" className="font-medium">
                                {ticket?.subject}
                            </Typography>
                        </div>
                        <div>
                            <Typography variant="small" className="text-gray-500 mb-2">Description</Typography>
                            <Typography className="text-gray-700">
                                {ticket?.description}
                            </Typography>
                        </div>

                        {(ticket?.attachments || []).length > 0 && (
                            <div className="mt-4">
                                <Typography variant="small" className="text-gray-500 mb-2">Attachments</Typography>
                                <div className="flex flex-wrap gap-2">
                                    {(ticket?.attachments || []).map((attachment, index) => (
                                        <a
                                            key={index}
                                            href={attachment.path}
                                            download={attachment.originalName}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title={`Download ${attachment?.originalName || 'attachment'}`}
                                            className="group inline-flex max-w-full items-center gap-2 rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-blue-50 hover:text-blue-700"
                                        >
                                            <Paperclip className="h-3.5 w-3.5 shrink-0 text-gray-400 group-hover:text-blue-500" />
                                            <span className="truncate">{attachment?.originalName || `Attachment ${index + 1}`}</span>
                                            <Download className="h-3.5 w-3.5 shrink-0 text-gray-400 group-hover:text-blue-600" />
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}

                        {(ticket?.tags || []).length > 0 && (
                            <div className="mt-4">
                                <Typography variant="small" className="text-gray-500 mb-2">Tags</Typography>
                                <div className="flex flex-wrap gap-2">
                                    {(ticket?.tags || []).map((tag, index) => (
                                        <Chip
                                            key={index}
                                            value={tag}
                                            icon={<Tag className="h-3 w-3" />}
                                            variant="outlined"
                                            size="sm"
                                        />
                                    ))}
                                </div>
                            </div>
                        )}

                        <hr className='my-4' />
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Typography variant="small" className="text-gray-500 mb-1">Created Date</Typography>
                                <Typography className="text-sm">{formatDate(ticket?.createdDate)}</Typography>
                            </div>
                            <div>
                                <Typography variant="small" className="text-gray-500 mb-1">Last Updated</Typography>
                                <Typography className="text-sm">{formatDate(ticket?.lastUpdated)}</Typography>
                            </div>
                        </div>

                        <hr className='my-4' />

                        <div>
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-2 p-1 bg-gray-100 rounded-lg">
                                    <Button
                                        size="sm"
                                        className="rounded-md"
                                        variant={activeTab === 'interactions' ? 'filled' : 'text'}
                                        onClick={() => setActiveTab('interactions')}
                                    >
                                        Interactions
                                    </Button>
                                    <Button
                                        size="sm"
                                        className="rounded-md"
                                        variant={activeTab === 'additional' ? 'filled' : 'text'}
                                        onClick={() => setActiveTab('additional')}
                                    >
                                        Additional Information
                                    </Button>
                                </div>
                                {/* Hidden — crowds the tab row on mobile. Auto-refresh itself is
                                    still active (useGetSupportTicketDetails polls every 15s). */}
                                {/* <Typography variant="small" className="text-gray-500">Auto-refresh: 15s</Typography> */}
                            </div>

                            {activeTab === 'interactions' && (
                                <div className="space-y-4">
                                    <div className="space-y-3 max-h-[420px] overflow-y-auto pr-2">
                                        {interactions.length === 0 && (
                                            <Typography className="text-sm text-gray-500">No interactions yet.</Typography>
                                        )}
                                        {interactions.map((interaction) => {
                                            const isCustomer = interaction?.user?.role === STATIC_USER_ROLES.USER;
                                            const isTeamMember = interaction?.user?.role === STATIC_USER_ROLES.TEAM_MEMBER;
                                            const isOwnerOrTeam = isCustomer || isTeamMember;
                                            const isFeedback = interaction?.isFeedback;

                                            if (isFeedback) {
                                                return (
                                                    <div key={interaction._id} className="flex justify-center my-4">
                                                        <div className="max-w-[90%] md:max-w-[70%] px-6 py-4 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 shadow-sm text-center">
                                                            <div className="flex justify-center mb-2">
                                                                <Rating value={interaction.rating || 5} readonly />
                                                            </div>
                                                            <div className="text-xs text-amber-700 mb-2 font-medium">
                                                                {interaction?.user?.name || 'Customer'} submitted feedback · {formatTimeAgo(interaction?.createdDate)}
                                                            </div>
                                                            <Typography className="text-sm text-gray-800 italic">
                                                                "{interaction?.message?.split('. Comment: ')[1] || 'No additional comments provided.'}"
                                                            </Typography>
                                                        </div>
                                                    </div>
                                                );
                                            }

                                            return (
                                                <div key={interaction._id} className={`flex ${isOwnerOrTeam ? 'justify-end' : 'justify-start'}`}>
                                                    <div className={`max-w-[80%] px-4 py-3 rounded-lg ${
                                                        isCustomer ? 'bg-blue-600 text-white' : 
                                                        isTeamMember ? 'bg-teal-600 text-white' : 
                                                        'bg-gray-100 border border-gray-200 text-gray-900'
                                                    }`}>
                                                        <div className={`text-xs mb-1 ${
                                                            isCustomer ? 'text-blue-100' : 
                                                            isTeamMember ? 'text-teal-100' : 
                                                            'text-gray-500'
                                                        }`}>
                                                            {interaction?.user?.name || 'Unknown'} · {formatTimeAgo(interaction?.createdDate)}
                                                            {!isOwnerOrTeam && interaction?.seenBy?.some(u => 
                                                                u.role === STATIC_USER_ROLES.USER || 
                                                                u.role === STATIC_USER_ROLES.TEAM_MEMBER || 
                                                                (u._id || u) === ticket?.customer?._id
                                                            ) && (
                                                                <span className="inline-flex items-center ml-2 text-teal-600" title="Seen by customer">
                                                                    <CheckCheck className="h-3.5 w-3.5" />
                                                                </span>
                                                            )}
                                                        </div>
                                                        {interaction?.message && (
                                                            <Typography className="text-sm leading-relaxed whitespace-pre-wrap">{interaction.message}</Typography>
                                                        )}
                                                        {(interaction?.attachments || []).length > 0 && (
                                                            <div className={`space-y-1 ${interaction?.message
                                                                ? `mt-3 border-t pt-2 ${isOwnerOrTeam ? 'border-white/25' : 'border-gray-200'}`
                                                                : ''}`}>
                                                                {interaction.attachments.map((attachment, attIndex) => (
                                                                    <a
                                                                        key={attIndex}
                                                                        href={attachment.path}
                                                                        download={attachment.originalName}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        title={`Download ${attachment.originalName}`}
                                                                        className="flex items-center gap-2 text-sm hover:underline"
                                                                    >
                                                                        <Paperclip className="h-3.5 w-3.5 shrink-0" />
                                                                        <span className="truncate">{attachment.originalName}</span>
                                                                        <Download className="h-3.5 w-3.5 shrink-0 opacity-70" />
                                                                    </a>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {![SUPPORT_TICKET_STATUSES.CLOSED, SUPPORT_TICKET_STATUSES.CANCELLED].includes(ticket?.status) && (
                                        <div className="border-t pt-4">
                                            <Typography variant="small" className="text-gray-600 mb-2">Add Reply</Typography>
                                            <Textarea
                                                label="Message"
                                                value={newMessage}
                                                onChange={(e) => setNewMessage(e.target.value)}
                                                rows={4}
                                            />
                                            <div className="flex items-start justify-between gap-3 mt-3">
                                                <AttachmentPicker
                                                    attachments={messageAttachments}
                                                    onChange={setMessageAttachments}
                                                    disabled={addInteractionMutation.isLoading}
                                                    label="Attach"
                                                />
                                                <Button
                                                    className="rounded-full flex shrink-0 items-center gap-2"
                                                    onClick={handleSendMessage}
                                                    disabled={(!newMessage.trim() && messageAttachments.length === 0) || addInteractionMutation.isLoading}
                                                >
                                                    {addInteractionMutation.isLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                                                    Send Reply
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {activeTab === 'additional' && (
                                <div className="grid grid-cols-2 gap-4">
                                    <div className='space-y-2'>
                                        <div className="p-3 bg-gray-50 rounded-lg">
                                            <Typography variant="small" className="text-gray-500 mb-1">Escalation Level</Typography>
                                            <div className="flex items-center gap-2">
                                                <Shield className="h-4 w-4 text-gray-600" />
                                                <Typography className="font-medium">Level {ticket?.escalationLevel || 0}</Typography>
                                            </div>
                                        </div>

                                        <div className="p-3 bg-gray-50 rounded-lg">
                                            <Typography variant="small" className="text-gray-500 mb-1">Customer Acknowledgment</Typography>
                                            <Chip
                                                value={ticket?.customerAcknowledgmentStatus === 'pending' ? 'Pending' : 'Acknowledged'}
                                                color={ticket?.customerAcknowledgmentStatus === 'pending' ? 'amber' : 'green'}
                                                size="sm"
                                            />
                                        </div>

                                        <div className="p-3 bg-gray-50 rounded-lg">
                                            <Typography variant="small" className="text-gray-500 mb-1">Requires Acknowledgment</Typography>
                                            <Chip
                                                value={ticket?.requiresCustomerAcknowledgment ? 'Yes' : 'No'}
                                                color={ticket?.requiresCustomerAcknowledgment ? 'blue' : 'gray'}
                                                size="sm"
                                            />
                                        </div>

                                        <div className="p-3 bg-gray-50 rounded-lg">
                                            <Typography variant="small" className="text-gray-500 mb-1">Customer Feedback</Typography>
                                            <Chip
                                                value={ticket?.customerFeedbackSubmitted ? 'Submitted' : 'Pending'}
                                                color={ticket?.customerFeedbackSubmitted ? 'green' : 'gray'}
                                                size="sm"
                                            />
                                        </div>
                                    </div>
                                    <div className='space-y-2'>
                                        <Card className='rounded-lg shadow-none border'>
                                            <CardBody>
                                                <div className="flex justify-between items-start mb-4">
                                                    <Typography variant="h6" className="flex items-center">
                                                        <UserPlus className="h-5 w-5 mr-2" />
                                                        Assigned Agent
                                                    </Typography>
                                                    <IconButton variant="text" size="sm" onClick={openAssignDialog}>
                                                        <Edit className="h-4 w-4" />
                                                    </IconButton>
                                                </div>

                                                {ticket?.assignedAgent ? (
                                                    <div className="space-y-3">
                                                        <div className="flex items-center">
                                                            <Avatar
                                                                src={`https://ui-avatars.com/api/?name=${ticket.assignedAgent.name}&background=0ea5e9&color=fff`}
                                                                alt={ticket.assignedAgent.name}
                                                                size="sm"
                                                                className="mr-3"
                                                            />
                                                            <div>
                                                                <Link to={`/user/${ticket.assignedAgent._id}`} className='underline hover:text-blue-600 duration-300'>
                                                                    <Typography className="font-medium">{ticket.assignedAgent.name}</Typography>
                                                                </Link>
                                                                <Typography variant="small" className="text-gray-500 text-blue-600">{ticket.assignedAgent.email}</Typography>
                                                            </div>
                                                        </div>

                                                        <div>
                                                            <Typography variant="small" className="text-gray-500 mb-1">Agent ID</Typography>
                                                            <Link to={`/user/${ticket.assignedAgent._id}`} className='text-gray-600 underline hover:text-blue-600 duration-300'>
                                                                <Typography className="font-mono text-xs">
                                                                    {ticket.assignedAgent._id}
                                                                </Typography>
                                                            </Link>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="text-center py-4">
                                                        <Typography variant="small" className="text-gray-500 mb-3">
                                                            No agent assigned
                                                        </Typography>
                                                        <Button size="sm" color="blue" onClick={openAssignDialog}>
                                                            Assign Agent
                                                        </Button>
                                                    </div>
                                                )}
                                            </CardBody>
                                        </Card>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className='space-y-4'>
                        <Card className='rounded-lg shadow-none border'>
                            <CardBody>
                                <div className="grid grid-cols-3 gap-2">
                                    <div>
                                        <p className="text-[11px] font-medium uppercase tracking-wider text-gray-500">Status</p>
                                        <div className={`mt-2 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 ${statusToneClasses[ticket?.status] || 'bg-gray-50 text-gray-700 border-gray-200'}`}>
                                            {React.cloneElement(statusConfig.icon, { className: 'h-3.5 w-3.5 shrink-0' })}
                                            <span className="truncate text-[11px] font-semibold uppercase tracking-wide">
                                                {ticket?.status ? convertToPresentableText(ticket.status) : '-'}
                                            </span>
                                        </div>
                                    </div>

                                    <div>
                                        <p className="text-[11px] font-medium uppercase tracking-wider text-gray-500">Priority</p>
                                        <div className={`mt-2 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 ${priorityToneClasses[ticket?.priority] || 'bg-gray-50 text-gray-700 border-gray-200'}`}>
                                            {React.cloneElement(priorityConfig.icon, { className: 'h-3.5 w-3.5 shrink-0' })}
                                            <span className="truncate text-[11px] font-semibold uppercase tracking-wide">{priorityConfig.label}</span>
                                        </div>
                                    </div>

                                    <div>
                                        <p className="text-[11px] font-medium uppercase tracking-wider text-gray-500">Category</p>
                                        <div className={`mt-2 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 ${categoryToneClasses[ticket?.category] || 'bg-gray-50 text-gray-700 border-gray-200'}`}>
                                            {React.cloneElement(categoryConfig.icon, { className: 'h-3.5 w-3.5 shrink-0' })}
                                            <span className="truncate text-[11px] font-semibold uppercase tracking-wide">
                                                {ticket?.category ? convertToPresentableText(ticket.category) : '-'}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-4 border-t pt-3">
                                    <p className="text-[11px] font-medium uppercase tracking-wider text-gray-500">Location (internal)</p>
                                    <button
                                        onClick={() => setLocationDialogOpen(true)}
                                        className="mt-2 flex w-full items-center gap-2 rounded-lg px-1 py-1 text-left text-sm transition-colors hover:bg-gray-50"
                                    >
                                        <MapPinIcon className="h-4 w-4 shrink-0 text-gray-400" />
                                        {ticket?.floor ? (
                                            <span className="text-gray-800">
                                                {[ticket.branch?.branchName, ticket.building?.name, ticket.floor?.number ? `Floor ${ticket.floor.number}` : null, ticket.space]
                                                    .filter(Boolean).join(' / ')}
                                            </span>
                                        ) : (
                                            <span className="text-amber-700">Not set — required to change status</span>
                                        )}
                                    </button>
                                </div>
                            </CardBody>
                        </Card>

                        <Card className='rounded-lg shadow-none border'>
                            <CardBody>
                                <Typography variant="h6" className="mb-4 flex items-center">
                                    <User className="h-5 w-5 mr-2" />
                                    Customer Information
                                </Typography>

                                <div className="space-y-3">
                                    <div>
                                        <Typography variant="small" className="text-gray-500 mb-1">Name</Typography>
                                        <Link to={`/customer/${ticket?.customer?._id}`} className='underline hover:text-blue-600 duration-300'>
                                            <Typography className="font-medium">{ticket?.customer?.name || '-'}</Typography>
                                        </Link>
                                    </div>

                                    <div>
                                        <Typography variant="small" className="text-gray-500 mb-1">Email</Typography>
                                        <div className="flex items-center">
                                            <Mail className="h-4 w-4 mr-2 text-gray-600" />
                                            <a href={`mailto:${ticket?.customer?.email || ''}`} className="text-blue-600 hover:underline text-sm">
                                                {ticket?.customer?.email || '-'}
                                            </a>
                                        </div>
                                    </div>

                                    <div>
                                        <Typography variant="small" className="text-gray-500 mb-1">Phone</Typography>
                                        <div className="flex items-center">
                                            <Phone className="h-4 w-4 mr-2 text-gray-600" />
                                            <a href={`tel:${ticket?.customer?.phone || ''}`} className="text-blue-600 hover:underline text-sm">
                                                {ticket?.customer?.phone || '-'}
                                            </a>
                                        </div>
                                    </div>

                                    <div>
                                        <Typography variant="small" className="text-gray-500 mb-1">Customer ID</Typography>
                                        <Link to={`/customer/${ticket?.customer?._id}`} className='text-gray-600 underline hover:text-blue-600 duration-300'>
                                            <Typography className="font-mono text-xs">
                                                {ticket?.customer?._id || '-'}
                                            </Typography>
                                        </Link>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>

                        {/* <Card className='rounded-lg shadow-none border'>
                            <CardBody>
                                <Typography variant="h6" className="mb-4">Quick Actions</Typography>
                                <div className="space-y-2">
                                    <Select
                                        label="Update Status"
                                        value={selectedStatus}
                                        onChange={(val) => setSelectedStatus(val)}
                                    >
                                        <Option value="open">Open</Option>
                                        <Option value="in_progress">In Progress</Option>
                                        <Option value="resolved">Resolved</Option>
                                        <Option value="closed">Closed</Option>
                                    </Select>

                                    <Button variant="outlined" className="w-full flex items-center justify-center gap-2" onClick={openDueDateDialog}>
                                        <Calendar className="h-4 w-4" />
                                        Set Due Date
                                    </Button>

                                    <Button variant="outlined" color="green" className="w-full flex items-center justify-center gap-2" onClick={openStatusDialog}>
                                        <CheckCircle className="h-4 w-4" />
                                        Update Ticket Status
                                    </Button>
                                </div>
                            </CardBody>
                        </Card> */}

                        <Card className='rounded-lg shadow-none border'>
                            <CardBody>
                                <Typography variant="h6" className="mb-4 flex items-center">
                                    <Hash className="h-5 w-5 mr-2" />
                                    Ticket Metadata
                                </Typography>

                                <div className="space-y-3 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Ticket ID</span>
                                        <span className="font-mono text-xs">{ticket?._id}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Version</span>
                                        <span>{ticket?.__v}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Created</span>
                                        <span>{formatTimeAgo(ticket?.createdDate)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Updated</span>
                                        <span>{formatTimeAgo(ticket?.lastUpdated)}</span>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                </div>
            </div>

            <Dialog open={statusDialogOpen} handler={setStatusDialogOpen}>
                <DialogHeader>Update Ticket Status</DialogHeader>
                <DialogBody className="space-y-4">
                    <Select label="Status" value={newStatus} onChange={(val) => setNewStatus(val)}>
                        <Option value={SUPPORT_TICKET_STATUSES.OPEN}>Open</Option>
                        <Option value={SUPPORT_TICKET_STATUSES.IN_PROGRESS}>In Progress</Option>
                        <Option value={SUPPORT_TICKET_STATUSES.PENDING_CUSTOMER}>Pending Customer</Option>
                        <Option value={SUPPORT_TICKET_STATUSES.RESOLVED}>Resolved</Option>
                        <Option value={SUPPORT_TICKET_STATUSES.CLOSED}>Closed</Option>
                        <Option value={SUPPORT_TICKET_STATUSES.CANCELLED}>Cancelled</Option>
                    </Select>
                    {(newStatus === SUPPORT_TICKET_STATUSES.RESOLVED || newStatus === SUPPORT_TICKET_STATUSES.CLOSED) && (
                        <Textarea label="Resolution" value={resolution} onChange={(e) => setResolution(e.target.value)} rows={4} />
                    )}
                </DialogBody>
                <DialogFooter className="gap-2">
                    <Button variant="outlined" onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
                    <Button onClick={submitStatusUpdate} disabled={updateTicketStatusMutation.isLoading}>
                        {updateTicketStatusMutation.isLoading ? 'Updating...' : 'Update'}
                    </Button>
                </DialogFooter>
            </Dialog>

            <Dialog open={assignDialogOpen} handler={setAssignDialogOpen}>
                <DialogHeader>Assign Agent</DialogHeader>
                <DialogBody>
                    <Select label="Agent" value={selectedAgent} onChange={(val) => setSelectedAgent(val)} disabled={agentsLoading}>
                        {agents?.users?.map((agent) => (
                            <Option key={agent._id} value={agent._id}>{agent.name} ({agent.email})</Option>
                        ))}
                    </Select>
                </DialogBody>
                <DialogFooter className="gap-2">
                    <Button variant="outlined" onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
                    <Button onClick={submitAssignment} disabled={!selectedAgent || assignTicketMutation.isLoading}>
                        {assignTicketMutation.isLoading ? 'Assigning...' : 'Assign'}
                    </Button>
                </DialogFooter>
            </Dialog>

            <TicketLocationModal
                isOpen={locationDialogOpen}
                onClose={closeLocationDialog}
                ticket={ticket}
                onConfirm={submitLocation}
                isLoading={updateTicketLocationMutation.isLoading || updateTicketStatusMutation.isLoading}
                title={pendingStatusChange ? 'Location Required' : 'Set Ticket Location'}
                description={pendingStatusChange
                    ? 'This ticket needs a location before its status can change. Set it below and the status will update at the same time.'
                    : undefined}
                confirmLabel={pendingStatusChange ? 'Save & Update Status' : 'Save Location'}
            />

            <Dialog open={priorityDialogOpen} handler={setPriorityDialogOpen}>
                <DialogHeader>Update Priority</DialogHeader>
                <DialogBody>
                    <Select label="Priority" value={newPriority} onChange={(value) => setNewPriority(value)}>
                        {SUPPORT_TICKET_PRIORITIES_ENUM.map((option) => (
                            <Option key={option} value={option}>
                                <span className="capitalize">{option}</span>
                            </Option>
                        ))}
                    </Select>
                </DialogBody>
                <DialogFooter className="gap-2">
                    <Button variant="outlined" onClick={() => setPriorityDialogOpen(false)}>Cancel</Button>
                    <Button
                        onClick={submitPriorityUpdate}
                        disabled={!newPriority || newPriority === ticket?.priority || updateTicketPriorityMutation.isLoading}
                    >
                        {updateTicketPriorityMutation.isLoading ? 'Saving...' : 'Update Priority'}
                    </Button>
                </DialogFooter>
            </Dialog>

            <Dialog open={dueDateDialogOpen} handler={setDueDateDialogOpen}>
                <DialogHeader>Set Due Date</DialogHeader>
                <DialogBody>
                    <Input type="date" label="Due Date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
                </DialogBody>
                <DialogFooter className="gap-2">
                    <Button variant="outlined" onClick={() => setDueDateDialogOpen(false)}>Cancel</Button>
                    <Button onClick={submitSetDueDate} disabled={!dueDate || updateTicketDueDateMutation.isLoading}>
                        {updateTicketDueDateMutation.isLoading ? 'Saving...' : 'Set Due Date'}
                    </Button>
                </DialogFooter>
            </Dialog>
        </>
    );
};

export default SupportDetailPage;
